import React from "react";
import { useSelector } from "react-redux";
import { Box, Grid, Stack, Typography } from "@mui/material";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import IndexApp from "../../Components/IndexApp";
import Layout from "../../Components/Layout";
import HomeHeader from "../../Components/Ui/HomeHeader";
import ContactSupportForm from "../../Components/App/Support/ContactSupportForm";
import { getSupportContact } from "../../config/supportContact";
import { useTailwindBrand } from "../../Components/shared";

/**
 * Page « Aide et contact » du tableau de bord commun (prof et client) :
 * coordonnées de l'équipe (mail, téléphone s'il est renseigné) et formulaire
 * de contact transmis au support (Slack + mail).
 */
const HelpPage = () => {
    const { brandConfig } = useTailwindBrand();
    const currentUser = useSelector((state: any) => state?.user?.currentUser);
    const account = currentUser?.isClient ? currentUser?.client : currentUser?.teachr;
    const contact = getSupportContact();
    const primary = brandConfig.colors.primary;
    const ff = brandConfig.fontFamily;

    const channel = (icon: React.ReactNode, title: string, value: React.ReactNode, hint?: string) => (
        <Box sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", p: 2, height: "100%" }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: `${primary}12`, color: primary, display: "flex" }}>{icon}</Box>
                <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ fontFamily: ff, color: "text.primary" }}>{title}</Typography>
                    <Typography variant="body2" sx={{ fontFamily: ff, color: primary, fontWeight: 600 }}>{value}</Typography>
                    {hint && <Typography variant="caption" sx={{ fontFamily: ff, color: "text.secondary" }}>{hint}</Typography>}
                </Box>
            </Stack>
        </Box>
    );

    return (
        <IndexApp
            navBar={{ index: 0 }}
            teachr={account}
            component={
                <Layout header={<HomeHeader title="🤝 Aide et contact" subtitle={`Une question, un problème ? L'équipe ${brandConfig.displayName} vous répond.`} />}>
                    <div className="bg-white rounded-md p-4 space-y-6">
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={contact.phone ? 4 : 6}>
                                {channel(
                                    <MailOutlineRoundedIcon fontSize="small" />,
                                    "Par mail",
                                    <a href={`mailto:${contact.email}`} style={{ color: "inherit", textDecoration: "none" }}>{contact.email}</a>,
                                    "Réponse sous un jour ouvré en général"
                                )}
                            </Grid>
                            {contact.phone && (
                                <Grid item xs={12} md={4}>
                                    {channel(
                                        <PhoneInTalkOutlinedIcon fontSize="small" />,
                                        "Par téléphone",
                                        <a href={`tel:${contact.phone}`} style={{ color: "inherit", textDecoration: "none" }}>{contact.phoneDisplay ?? contact.phone}</a>,
                                        contact.hours
                                    )}
                                </Grid>
                            )}
                            <Grid item xs={12} md={contact.phone ? 4 : 6}>
                                {channel(
                                    <ChatBubbleOutlineRoundedIcon fontSize="small" />,
                                    "Depuis votre espace",
                                    "Le formulaire ci-dessous",
                                    "Votre demande arrive directement à l'équipe, avec vos coordonnées"
                                )}
                            </Grid>
                        </Grid>

                        <Box sx={{ maxWidth: 720 }}>
                            <Typography variant="h6" fontWeight={700} sx={{ fontFamily: ff, color: primary, mb: 2 }}>
                                Nous écrire
                            </Typography>
                            <ContactSupportForm />
                        </Box>
                    </div>
                </Layout>
            }
        />
    );
};

export default HelpPage;
